import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MapPin, Phone, Building2, CalendarClock, Clock, Timer, Tag, Hash,
  Plus, Trash2, Upload, ListChecks, Package, MessageSquare, Send, ImageIcon, Lock, ChevronsUpDown, Camera, Images, X, ZoomIn, Pencil, Navigation, ArrowLeftRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { compressImage, compressToDataUrl } from "@/lib/image";
import { useAuth } from "@/context/AuthContext";
import { useStatuses } from "@/context/StatusContext";
import { AttachmentLightbox, attUrl } from "@/components/AttachmentLightbox";
import { StatoBadge, PrioritaBadge } from "@/components/Badges";
import { STATUS } from "@/lib/constants";
import { endTime, fmtDate, fmtDateTime } from "@/lib/time";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ProductCombobox } from "@/components/ProductCombobox";
import { SignaturePad } from "@/components/SignaturePad";
import { cn } from "@/lib/utils";

function MatInfo({ m }) {
  return (
    <>
      <p className="truncate text-sm font-semibold text-navy">{m.name || m.description || "—"}</p>
      <p className="font-mono text-[11px] text-muted-foreground">Cod. produttore: {m.manufacturer_code || "—"} · Fornitore: {m.supplier_name || "—"}</p>
    </>
  );
}

export default function MobileTicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { options: statusOptions, reasons: suspensionReasons, koReasons } = useStatuses();
  const [t, setT] = useState(null);
  const [atts, setAtts] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [editNoteId, setEditNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [winv, setWinv] = useState([]);
  const [serialSel, setSerialSel] = useState({});
  const [selProd, setSelProd] = useState("");
  const [otherSerials, setOtherSerials] = useState([]);
  const [otherQty, setOtherQty] = useState(1);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [tab, setTab] = useState("dettagli");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [sig, setSig] = useState("");
  const [completing, setCompleting] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendMotivo, setSuspendMotivo] = useState("");
  const [koOpen, setKoOpen] = useState(false);
  const [koNote, setKoNote] = useState("");
  const [koMotivo, setKoMotivo] = useState("");
  const [checklist, setChecklist] = useState({ caratterizzazione_done: null, caratterizzazione_reason: "", caratterizzazione_screenshot: "", srd_done: null, lavorazioni_eseguite: "" });
  const clFileRef = useRef(null);
  const [attivitaList, setAttivitaList] = useState([]);
  const [selAtt, setSelAtt] = useState("");
  const [uploadSheet, setUploadSheet] = useState(false);
  const fileRef = useRef(null);
  const galleryRef = useRef(null);
  const [timeForm, setTimeForm] = useState({ start: "", end: "" });
  const [phone, setPhone] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [stdQty, setStdQty] = useState({});
  const [stdItems, setStdItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [interMap, setInterMap] = useState({});

  const loadAtts = () => api.get(`/tickets/${id}/attachments`).then((r) => setAtts(r.data)).catch(() => {});
  useEffect(() => {
    api.get(`/tickets/${id}`).then((r) => setT(r.data)).catch(() => {});
    api.get("/attivita-eseguite").then((r) => setAttivitaList(r.data)).catch(() => {});
    api.get("/settings/standard-skus").then((r) => { if (Array.isArray(r.data?.items)) setStdItems(r.data.items); }).catch(() => {});
    api.get("/magazzino/products").then((r) => setCatalog(r.data || [])).catch(() => setCatalog([]));
    api.get("/product-groups/map").then((r) => setInterMap(r.data || {})).catch(() => setInterMap({}));
    loadAtts();
  }, [id]);
  useEffect(() => {
    if (!t) return;
    setTimeForm({ start: t.start_time || "", end: t.start_time ? endTime(t) : "" });
    setPhone(t.client_phone || "");
  }, [t?.start_time, t?.duration_min, t?.client_phone]);
  useLiveRefresh(() => {
    api.get(`/tickets/${id}`).then((r) => setT(r.data)).catch(() => {});
    loadAtts();
  });

  useEffect(() => {
    if (user?.magazzino_warehouse_id) api.get("/magazzino/inventory", { params: { warehouse_id: user.magazzino_warehouse_id } }).then((r) => setWinv(r.data)).catch(() => setWinv([]));
  }, [user?.magazzino_warehouse_id]);

  const serialsForProduct = (pid) => (winv || []).filter((r) => r.product_id === pid && r.serial_number).map((r) => r.serial_number);
  const toggleSerial = (pid, s) => setSerialSel((prev) => {
    const cur = prev[pid] || [];
    return { ...prev, [pid]: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] };
  });
  const confirmInstall = async (pm) => {
    const serials = serialSel[pm.product_id] || [];
    const qty = serials.length || pm.qty || 1;
    try {
      const { data } = await api.post(`/tickets/${id}/materials`, {
        product_id: pm.product_id, sku: pm.sku, name: pm.name, description: pm.name || pm.description,
        manufacturer_code: pm.manufacturer_code || "", supplier_name: pm.supplier_name || "",
        unit: pm.unit || "pz", qty, price: pm.price || 0, serial_numbers: serials,
      });
      setT(data); setSerialSel((prev) => ({ ...prev, [pm.product_id]: [] })); toast.success("Materiale installato registrato");
    } catch (e) { toast.error(apiError(e)); }
  };

  const warehouseProducts = useMemo(() => {
    const map = {};
    (winv || []).forEach((r) => {
      const k = r.product_id;
      if (!map[k]) map[k] = { product_id: k, sku: r.sku, name: r.name, manufacturer_code: r.manufacturer_code, supplier_name: r.supplier_name, is_serialized: r.is_serialized, unit: r.unit_of_measure || "pz", price: r.price || 0, serials: [], qty: 0 };
      map[k].qty += r.stock || 0;
      if (r.serial_number) map[k].serials.push(r.serial_number);
    });
    return Object.values(map).filter((p) => p.qty > 0 || p.serials.length).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [winv]);
  const curProd = warehouseProducts.find((p) => p.product_id === selProd);
  const toggleOtherSerial = (s) => setOtherSerials((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  const addFromWarehouse = async () => {
    if (!curProd) { toast.error("Seleziona un materiale"); return; }
    const serials = curProd.is_serialized ? otherSerials : [];
    if (curProd.is_serialized && serials.length === 0) { toast.error("Seleziona almeno un seriale"); return; }
    const qty = curProd.is_serialized ? serials.length : (parseFloat(otherQty) || 1);
    try {
      const { data } = await api.post(`/tickets/${id}/materials`, {
        product_id: curProd.product_id, sku: curProd.sku, name: curProd.name,
        manufacturer_code: curProd.manufacturer_code || "", supplier_name: curProd.supplier_name || "",
        description: curProd.name, unit: curProd.unit, qty, price: curProd.price || 0, serial_numbers: serials,
      });
      setT(data); setSelProd(""); setOtherSerials([]); setOtherQty(1); toast.success("Materiale aggiunto");
    } catch (e) { toast.error(apiError(e)); }
  };

  const standardItems = useMemo(() => (stdItems || []).map((cfg) => {
    const sku = cfg.sku;
    const up = (sku || "").toUpperCase();
    const inv = warehouseProducts.find((w) => (w.sku || "").toUpperCase() === up);
    const cat = (catalog || []).find((p) => (p.sku || "").toUpperCase() === up);
    const name = cat?.name || inv?.name || sku;
    const manufacturer_code = cat?.manufacturer_code || inv?.manufacturer_code || "";
    const supplier_name = cat?.supplier_name || inv?.supplier_name || "";
    const unit = inv?.unit || cat?.unit_of_measure || "pz";
    const avail = inv?.qty || 0;
    const defaultQty = Math.max(1, parseInt(cfg.default_qty) || 1);
    const product = inv || (cat ? { product_id: cat.id, sku: cat.sku, name, manufacturer_code, supplier_name, unit, price: cat.price || 0, qty: 0 } : null);
    return { sku, name, manufacturer_code, supplier_name, unit, avail, defaultQty, product };
  }), [warehouseProducts, catalog, stdItems]);
  const addStandard = async (sku, product) => {
    const cfg = (stdItems || []).find((x) => x.sku === sku);
    const def = Math.max(1, parseInt(cfg?.default_qty) || 1);
    const qty = parseInt(stdQty[sku]) || def;
    if (!product) { toast.error(`${sku}: non presente nella tua giacenza`); return; }
    if (qty > (product.qty || 0)) { toast.error(`${sku}: giacenza non sufficiente (disponibili ${product.qty})`); return; }
    try {
      const { data } = await api.post(`/tickets/${id}/materials`, {
        product_id: product.product_id, sku: product.sku, name: product.name,
        manufacturer_code: product.manufacturer_code || "", supplier_name: product.supplier_name || "",
        description: product.name, unit: product.unit, qty, price: product.price || 0, serial_numbers: [],
      });
      setT(data); setStdQty((s) => ({ ...s, [sku]: 1 })); toast.success(`${product.name} aggiunto`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeStatus = async (status, extra = {}) => {
    try { const { data } = await api.post(`/tickets/${id}/status`, { status, ...extra }); setT(data); toast.success("Stato aggiornato"); return true; }
    catch (e) { toast.error(apiError(e)); return false; }
  };
  const isPlannedSatisfied = (pm, installedIds) => {
    if (!pm.product_id) return true;
    const group = interMap[pm.product_id] || [pm.product_id];
    return group.some((g) => installedIds.has(g));
  };
  const pendingMaterials = () => {
    const installedIds = new Set((t?.materials_used || []).map((m) => m.product_id).filter(Boolean));
    return (t?.planned_materials || []).filter((pm) => pm.product_id && !isPlannedSatisfied(pm, installedIds));
  };
  const substituteForName = (pid) => {
    if (!pid) return null;
    const planned = t?.planned_materials || [];
    if (planned.some((p) => p.product_id === pid)) return null; // installato = pianificato, non è un sostituto
    const group = interMap[pid] || [pid];
    const a = planned.find((p) => p.product_id && group.includes(p.product_id));
    return a ? a.name : null;
  };
  const equivalentsInStock = (pm) => {
    const group = interMap[pm.product_id] || [];
    return group.filter((g) => g !== pm.product_id)
      .map((g) => warehouseProducts.find((w) => w.product_id === g))
      .filter((w) => w && (w.serials.length > 0 || w.qty > 0));
  };
  const onSelectStatus = (status) => {
    setSheetOpen(false);
    if (status === "completato") {
      // Verifica materiali/seriali PRIMA della check-list: se ci sono apparati suggeriti non ancora
      // scaricati, blocca subito e porta il tecnico al consuntivo (niente check-list da ripetere).
      const pend = pendingMaterials();
      if (pend.length) {
        setTab("consuntivo");
        toast.error(`Prima di completare scarica gli apparati installati (con i seriali): ${pend.map((p) => p.name).join(", ")}`);
        setTimeout(() => document.querySelector('[data-testid="mobile-materials-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
        return;
      }
      setConfirmOpen(true); return;
    }
    if (status === "sospeso") { setSuspendReason(""); setSuspendMotivo(""); setSuspendOpen(true); return; }
    if (status === "ko") { setKoNote(""); setKoMotivo(""); setKoOpen(true); return; }
    changeStatus(status);
  };
  const submitKo = async () => {
    if (!koMotivo) { toast.error("Seleziona un motivo di KO"); return; }
    if (!koNote.trim()) { toast.error("Indica il motivo del KO"); return; }
    const ok = await changeStatus("ko", { note: koNote, ko_reason: koMotivo });
    if (ok) setKoOpen(false);
  };
  const submitSuspend = async () => {
    if (!suspendMotivo) { toast.error("Seleziona un motivo di sospensione"); return; }
    if (!suspendReason.trim()) { toast.error("Scrivi una nota di sospensione"); return; }
    const ok = await changeStatus("sospeso", { note: suspendReason, suspension_reason: suspendMotivo });
    if (ok) setSuspendOpen(false);
  };
  const proceedFromChecklist = () => {
    const c = checklist;
    if (c.caratterizzazione_done === null || c.srd_done === null) { toast.error("Rispondi a tutte le domande della check-list"); return; }
    if (c.caratterizzazione_done && !c.caratterizzazione_screenshot) { toast.error("Carica lo screenshot della caratterizzazione effettuata"); return; }
    if (!c.caratterizzazione_done) {
      if (!c.caratterizzazione_reason.trim()) { toast.error("Indica il motivo per cui non è possibile la caratterizzazione"); return; }
      if (!c.caratterizzazione_screenshot) { toast.error("Carica uno screenshot a supporto del motivo"); return; }
    }
    if (!c.lavorazioni_eseguite.trim()) { toast.error("Descrivi le lavorazioni eseguite"); return; }
    setChecklistOpen(false); setSig(""); setSignOpen(true);
  };
  const onChecklistFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await compressToDataUrl(f);
      setChecklist((c) => ({ ...c, caratterizzazione_screenshot: url }));
    } catch { toast.error("Impossibile leggere l'immagine"); }
    e.target.value = "";
  };
  const submitCompletion = async () => {
    setCompleting(true);
    const ok = await changeStatus("completato", { signature: sig, checklist });
    setCompleting(false);
    if (ok) { setSignOpen(false); setSig(""); }
  };

  const perf = t?.performed_activities || [];
  const savePerf = async (list) => {
    try { const { data } = await api.put(`/tickets/${id}`, { performed_activities: list }); setT(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addPerf = () => {
    const name = attivitaList.find((a) => a.id === selAtt)?.name;
    if (!name || perf.includes(name)) { setSelAtt(""); return; }
    savePerf([...perf, name]); setSelAtt("");
  };
  const delPerf = (name) => savePerf(perf.filter((x) => x !== name));

  const saveTime = async () => {
    if (!timeForm.start || !timeForm.end) { toast.error("Inserisci orario di inizio e fine"); return; }
    const [sh, sm] = timeForm.start.split(":").map(Number);
    const [eh, em] = timeForm.end.split(":").map(Number);
    const dur = (eh * 60 + em) - (sh * 60 + sm);
    if (dur <= 0) { toast.error("La fine deve essere successiva all'inizio"); return; }
    try { const { data } = await api.put(`/tickets/${id}`, { start_time: timeForm.start, duration_min: dur }); setT(data); toast.success("Orario intervento aggiornato"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const savePhone = async () => {
    try { const { data } = await api.put(`/tickets/${id}`, { client_phone: phone }); setT(data); toast.success("Recapito aggiornato"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const delMaterial = async (mid) => { try { const { data } = await api.delete(`/tickets/${id}/materials/${mid}`); setT(data); } catch (e) { toast.error(apiError(e)); } };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try { const { data } = await api.post(`/tickets/${id}/notes`, { text: noteText }); setT(data); setNoteText(""); toast.success("Nota aggiunta"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const canModifyNote = (n) => n.author_id === user?.id || (n.role === "tecnico" && n.author === user?.name);
  const saveNoteEdit = async (nid) => {
    if (!editNoteText.trim()) return;
    try { const { data } = await api.put(`/tickets/${id}/notes/${nid}`, { text: editNoteText }); setT(data); setEditNoteId(null); setEditNoteText(""); toast.success("Nota aggiornata"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteNote = async (nid) => {
    if (!window.confirm("Eliminare questa nota?")) return;
    try { const { data } = await api.delete(`/tickets/${id}/notes/${nid}`); setT(data); toast.success("Nota eliminata"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (e) => {
    const raw = e.target.files?.[0]; if (!raw) return;
    const file = await compressImage(raw);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/tickets/${id}/attachments`, fd); toast.success("Foto caricata"); loadAtts(); } catch (er) { toast.error(apiError(er)); }
    e.target.value = "";
  };
  const delAtt = async (aid) => { try { await api.delete(`/attachments/${aid}`); loadAtts(); } catch (er) { toast.error(apiError(er)); } };

  if (!t) return <Skeleton className="h-96 rounded-xl" />;

  const matTotal = (t.materials_used || []).reduce((s, m) => s + (m.price || 0) * (m.qty || 0), 0);
  const locked = t.status === "completato";

  const Row = ({ icon: Icon, label, value }) => value ? (
    <div className="flex items-start gap-2.5 py-1.5">
      <Icon size={16} className="mt-0.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0"><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p><div className="text-sm font-semibold text-navy">{value}</div></div>
    </div>
  ) : null;

  return (
    <>
    <div className="space-y-4 animate-fade-up pb-4">
      <button onClick={() => nav("/m/agenda")} className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground" data-testid="mobile-back">
        <ArrowLeft size={16} /> Torna all'agenda
      </button>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs font-bold text-primary">{t.code}</span>
          {t.id_olo && <span className="font-mono text-[11px] font-semibold text-muted-foreground">· ID OLO {t.id_olo}</span>}
        </div>
        <h1 className="mt-1 text-xl font-extrabold tracking-tight text-navy">{t.client_name}</h1>
        <p className="text-sm text-muted-foreground">{t.category}</p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Priorità</p>
            <PrioritaBadge priorita={t.priority} />
          </div>
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Stato intervento</p>
            <StatoBadge stato={t.status} />
          </div>
          {locked && <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><Lock size={12} />consuntivo bloccato</span>}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="dettagli" data-testid="mtab-dettagli">Dettagli</TabsTrigger>
          <TabsTrigger value="consuntivo" data-testid="mtab-consuntivo">Consuntivo</TabsTrigger>
          <TabsTrigger value="foto" data-testid="mtab-foto">Foto & Note</TabsTrigger>
        </TabsList>

        {/* DETTAGLI */}
        <TabsContent value="dettagli" className="mt-3">
          <div className="rounded-xl border border-border bg-card p-4 divide-y divide-border/60">
            <Row icon={Building2} label="Committente" value={t.committente} />
            <Row icon={Tag} label="Categoria" value={t.category} />
            <Row icon={Hash} label="ID OLO" value={t.id_olo} />
            <Row icon={Hash} label="ID Risorsa" value={t.id_risorsa} />
            <Row icon={Hash} label="PFS" value={t.pfs} />
            <Row icon={Tag} label="Picco" value={t.picco} />
            <Row icon={MapPin} label="Indirizzo" value={[t.address, t.comune].filter(Boolean).join(", ") ? <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([t.address, t.comune].filter(Boolean).join(", "))}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary underline" data-testid="detail-map-link">{[t.address, t.comune].filter(Boolean).join(", ")}<Navigation size={13} /></a> : null} />
            <Row icon={Phone} label="Telefono" value={t.client_phone ? <a href={`tel:${t.client_phone}`} className="text-primary underline">{t.client_phone}</a> : null} />
            <Row icon={CalendarClock} label="Appuntamento cliente" value={t.scheduled_date ? `${fmtDate(t.scheduled_date)} ${t.appointment_time || ""}`.trim() : null} />
            <Row icon={CalendarClock} label="Intervento previsto" value={(t.intervention_date || t.start_time) ? <span className="font-bold text-primary">{[fmtDate(t.intervention_date), t.start_time ? `${t.start_time}–${endTime(t)}` : ""].filter(Boolean).join(" · ")}</span> : null} />
            <Row icon={Timer} label="Durata" value={t.duration_min ? `${t.duration_min} min` : null} />
            <Row icon={MessageSquare} label="Descrizione" value={t.description} />
            <Row icon={Tag} label="Stato" value={
              <span className="flex flex-wrap items-center gap-2">
                <StatoBadge stato={t.status} />
                {t.status === "sospeso" && t.suspension_reason && <span className="text-xs font-semibold text-red-600">Motivo: {t.suspension_reason}</span>}
                {t.status === "ko" && t.ko_reason && <span className="text-xs font-semibold text-red-800">Motivo: {t.ko_reason}</span>}
              </span>
            } />
          </div>

          {/* Orario previsto intervento - modificabile dal tecnico */}
          <div className="mt-3 rounded-xl border border-border bg-card p-4">
            <p className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"><Clock size={13} />Orario previsto intervento</p>
            {t.intervention_date && <p className="mb-2 text-sm font-bold text-primary" data-testid="mobile-intervention-date"><CalendarClock size={13} className="mr-1 inline" />Data intervento {fmtDate(t.intervention_date)}</p>}
            {locked ? (
              <p className="text-sm text-navy">{t.start_time ? `${t.start_time}–${endTime(t)}` : "—"}</p>
            ) : (
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="text-[11px] text-muted-foreground">Inizio</label>
                  <Input type="time" value={timeForm.start} onChange={(e) => setTimeForm({ ...timeForm, start: e.target.value })} data-testid="mobile-time-start" />
                </div>
                <div className="flex-1">
                  <label className="text-[11px] text-muted-foreground">Fine</label>
                  <Input type="time" value={timeForm.end} onChange={(e) => setTimeForm({ ...timeForm, end: e.target.value })} data-testid="mobile-time-end" />
                </div>
                <Button size="sm" onClick={saveTime} data-testid="mobile-time-save">Salva</Button>
              </div>
            )}
          </div>

          {/* Recapito telefonico - modificabile dal tecnico */}
          <div className="mt-3 rounded-xl border border-border bg-card p-4">
            <p className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"><Phone size={13} />Recapito telefonico</p>
            {locked ? (
              <p className="text-sm text-navy">{t.client_phone || "—"}</p>
            ) : (
              <div className="flex items-end gap-2">
                <Input type="tel" className="flex-1" placeholder="Es. 333 1234567" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="mobile-phone-input" />
                <Button size="sm" onClick={savePhone} data-testid="mobile-phone-save">Salva</Button>
              </div>
            )}
          </div>

          {/* Password apparati e ID Servizi - sola lettura per il tecnico */}
          {(t.device_password || t.id_servizi) && (
            <div className="mt-3 rounded-xl border border-border bg-card p-4" data-testid="mobile-device-pwd">
              {t.device_password && (
                <div className="mb-3">
                  <p className="mb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"><Hash size={13} />Password apparati</p>
                  <pre className="max-h-32 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-secondary/30 p-2.5 font-mono text-sm text-navy" data-testid="mobile-device-pwd-value">{t.device_password}</pre>
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => { navigator.clipboard.writeText(t.device_password); toast.success("Copiato"); }} data-testid="mobile-device-pwd-copy"><ImageIcon size={14} />Copia</Button>
                </div>
              )}
              {t.id_servizi && (
                <div>
                  <p className="mb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"><Hash size={13} />ID Servizi</p>
                  <pre className="max-h-32 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-secondary/30 p-2.5 font-mono text-sm text-navy" data-testid="mobile-id-servizi-value">{t.id_servizi}</pre>
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => { navigator.clipboard.writeText(t.id_servizi); toast.success("Copiato"); }} data-testid="mobile-id-servizi-copy"><ImageIcon size={14} />Copia</Button>
                </div>
              )}
            </div>
          )}
          {t.description && (
            <div className="mt-3 rounded-xl border border-border bg-card p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Descrizione</p>
              <p className="mt-1 text-sm text-navy">{t.description}</p>
            </div>
          )}
          {(t.planned_materials || []).length > 0 && (
            <div className="mt-3 rounded-xl border border-border bg-card p-4" data-testid="mobile-dettagli-planned">
              <div className="mb-2 flex items-center gap-2"><Package size={16} className="text-primary" /><h3 className="text-sm font-bold text-navy">Materiali da installare (suggeriti)</h3></div>
              <div className="space-y-2">
                {(t.planned_materials || []).map((pm, idx) => (
                  <div key={idx} className="rounded-lg border border-border bg-secondary/20 p-2.5">
                    <p className="text-sm font-semibold text-navy">{pm.name || pm.description}</p>
                    <p className="font-mono text-xs text-muted-foreground">{pm.sku} · previsti {pm.qty} {pm.unit}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* CONSUNTIVO */}
        <TabsContent value="consuntivo" className="mt-3 space-y-4">
          <div className="rounded-xl border border-border bg-card p-4" data-testid="mobile-materials-section">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2"><Package size={16} className="text-primary" /><h3 className="text-sm font-bold text-navy">Materiali</h3></div>
              <span className="text-xs font-bold text-navy">€ {matTotal.toFixed(2)}</span>
            </div>

            {(() => {
              const installedIds = new Set((t.materials_used || []).map((m) => m.product_id).filter(Boolean));
              const plannedRemaining = (t.planned_materials || []).filter((pm) => !isPlannedSatisfied(pm, installedIds));
              return (
                <>
                  {(t.materials_used || []).length > 0 && (
                    <div className="mb-3 space-y-2">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Installati</p>
                      {(t.materials_used || []).map((m) => (
                        <div key={m.id} className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-2.5" data-testid={`mobile-material-${m.id}`}>
                          <div className="flex items-center justify-between">
                            <div className="min-w-0">
                              <MatInfo m={m} />
                              <p className="mt-0.5 text-xs text-muted-foreground">{m.qty} {m.unit}</p>
                              {substituteForName(m.product_id) && (
                                <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700" data-testid={`mobile-substitute-${m.id}`}>
                                  <ArrowLeftRight size={11} />in sostituzione del pianificato {substituteForName(m.product_id)}
                                </span>
                              )}
                            </div>
                            {!locked && <button onClick={() => delMaterial(m.id)} className="ml-2 shrink-0 text-muted-foreground active:text-destructive" data-testid={`mobile-material-del-${m.id}`}><Trash2 size={16} /></button>}
                          </div>
                          {(m.serial_numbers || []).length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {m.serial_numbers.map((s) => <span key={s} className="rounded bg-secondary/60 px-1.5 py-0.5 font-mono text-[10px] text-navy">{s}</span>)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {!locked && (<>
                    {plannedRemaining.length > 0 && (
                      <>
                        <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Da installare — scegli i seriali</p>
                        <div className="space-y-3">
                          {plannedRemaining.map((pm, idx) => {
                            const avail = serialsForProduct(pm.product_id);
                            const sel = serialSel[pm.product_id] || [];
                            return (
                              <div key={pm.product_id || idx} className="rounded-lg border border-border bg-secondary/20 p-3" data-testid={`mobile-planned-${idx}`}>
                                <MatInfo m={pm} />
                                <p className="mb-2 mt-0.5 text-xs text-muted-foreground">Previsti {pm.qty} {pm.unit}</p>
                                {avail.length ? (
                                  <>
                                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Seriali dal tuo magazzino ({sel.length} selez.)</p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {avail.map((s) => (
                                        <button key={s} onClick={() => toggleSerial(pm.product_id, s)} data-testid={`serial-${s}`}
                                          className={cn("rounded-md border px-2 py-1 font-mono text-xs transition-colors", sel.includes(s) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-navy")}>
                                          {s}
                                        </button>
                                      ))}
                                    </div>
                                    <Button size="sm" className="mt-2 w-full" onClick={() => confirmInstall(pm)} data-testid={`confirm-install-${idx}`} disabled={sel.length === 0}>
                                      <Plus size={15} />Sposta in installati {sel.length ? `(${sel.length})` : ""}
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs font-semibold text-amber-700">
                                      {user?.magazzino_warehouse_id ? "Nessun seriale disponibile nel tuo magazzino per questo prodotto" : "Magazzino mobile non collegato al tuo account"}
                                    </p>
                                    {equivalentsInStock(pm).length > 0 && (
                                      <div className="mt-2 space-y-2 rounded-md border border-indigo-200 bg-indigo-50/40 p-2" data-testid={`mobile-equivalents-${idx}`}>
                                        <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-indigo-700"><ArrowLeftRight size={11} />Apparati equivalenti disponibili nel tuo magazzino</p>
                                        {equivalentsInStock(pm).map((eq) => {
                                          const eqSel = serialSel[eq.product_id] || [];
                                          const target = { product_id: eq.product_id, sku: eq.sku, name: eq.name, unit: eq.unit, price: eq.price, qty: pm.qty };
                                          return (
                                            <div key={eq.product_id} className="rounded-md border border-indigo-200 bg-card p-2" data-testid={`mobile-equivalent-${eq.product_id}`}>
                                              <p className="text-xs font-semibold text-navy">{eq.name}{eq.sku ? ` · ${eq.sku}` : ""}</p>
                                              {eq.serials.length > 0 ? (
                                                <>
                                                  <p className="mb-1 mt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Seriali ({eqSel.length} selez.)</p>
                                                  <div className="flex flex-wrap gap-1.5">
                                                    {eq.serials.map((s) => (
                                                      <button key={s} onClick={() => toggleSerial(eq.product_id, s)} data-testid={`eq-serial-${s}`}
                                                        className={cn("rounded-md border px-2 py-1 font-mono text-xs transition-colors", eqSel.includes(s) ? "border-indigo-600 bg-indigo-600 text-white" : "border-border bg-card text-navy")}>
                                                        {s}
                                                      </button>
                                                    ))}
                                                  </div>
                                                  <Button size="sm" variant="secondary" className="mt-2 w-full" onClick={() => confirmInstall(target)} data-testid={`eq-install-${eq.product_id}`} disabled={eqSel.length === 0}>
                                                    <Plus size={15} />Installa {eq.name.split(" ")[0]} in sostituzione {eqSel.length ? `(${eqSel.length})` : ""}
                                                  </Button>
                                                </>
                                              ) : (
                                                <Button size="sm" variant="secondary" className="mt-2 w-full" onClick={() => confirmInstall(target)} data-testid={`eq-install-${eq.product_id}`}>
                                                  <Plus size={15} />Installa in sostituzione ({pm.qty} {pm.unit})
                                                </Button>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {standardItems.length > 0 && (
                    <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="std-materials-block">
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-primary">Materiali standard impianto</p>
                      <div className="space-y-2">
                        {standardItems.map(({ sku, name, manufacturer_code, supplier_name, unit, avail, defaultQty, product }) => {
                          const insufficient = avail < 1;
                          return (
                            <div key={sku} className="rounded-lg border border-border bg-card p-2.5" data-testid={`std-material-${sku}`}>
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-navy" data-testid={`std-name-${sku}`}>{name}</p>
                                  <p className="font-mono text-[11px] text-muted-foreground">SKU {sku} · disp. {avail} {unit}</p>
                                </div>
                                {!insufficient && (
                                  <div className="flex shrink-0 items-center gap-1.5">
                                    <Input type="number" min="1" max={avail} className="h-8 w-16" value={stdQty[sku] ?? defaultQty} onChange={(e) => setStdQty((s) => ({ ...s, [sku]: e.target.value }))} data-testid={`std-qty-${sku}`} />
                                    <Button size="sm" onClick={() => addStandard(sku, product)} data-testid={`std-add-${sku}`}><Plus size={14} /></Button>
                                  </div>
                                )}
                              </div>
                              {insufficient && <p className="mt-1.5 rounded bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700" data-testid={`std-warn-${sku}`}>{user?.magazzino_warehouse_id ? "Giacenza non sufficiente nel tuo magazzino" : "Magazzino mobile non collegato"}</p>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    )}

                    <div className="mt-4 border-t border-border pt-3">
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Aggiungi altro materiale dal tuo magazzino</p>
                      {!user?.magazzino_warehouse_id ? (
                        <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs font-semibold text-amber-700">Magazzino mobile non collegato al tuo account</p>
                      ) : warehouseProducts.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Nessun materiale in giacenza nel tuo magazzino.</p>
                      ) : (
                        <div className="space-y-2">
                          <ProductCombobox products={warehouseProducts} value={selProd} onChange={(v) => { setSelProd(v); setOtherSerials([]); setOtherQty(1); }} testid="mobile-other-prod" />
                          {curProd && curProd.is_serialized ? (
                            <>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Seriali ({otherSerials.length} selez.)</p>
                              <div className="flex flex-wrap gap-1.5">
                                {curProd.serials.map((s) => (
                                  <button key={s} onClick={() => toggleOtherSerial(s)} data-testid={`other-serial-${s}`}
                                    className={cn("rounded-md border px-2 py-1 font-mono text-xs transition-colors", otherSerials.includes(s) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-navy")}>{s}</button>
                                ))}
                              </div>
                            </>
                          ) : curProd ? (
                            <Input type="number" min="0" step="1" placeholder="Q.tà" value={otherQty} onChange={(e) => setOtherQty(e.target.value)} data-testid="mobile-other-qty" />
                          ) : null}
                          <Button size="sm" className="w-full" onClick={addFromWarehouse} data-testid="mobile-other-add" disabled={!curProd}><Plus size={15} />Aggiungi materiale</Button>
                        </div>
                      )}
                    </div>
                  </>)}

                  {(t.materials_used || []).length === 0 && plannedRemaining.length === 0 && locked && (
                    <p className="text-sm text-muted-foreground">Nessun materiale registrato.</p>
                  )}
                </>
              );
            })()}
          </div>
        </TabsContent>

        {/* FOTO & NOTE */}
        <TabsContent value="foto" className="mt-3 space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2"><ImageIcon size={16} className="text-primary" /><h3 className="text-sm font-bold text-navy">Foto</h3></div>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="mobile-att-input" />
              <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="mobile-att-gallery-input" />
              <Button variant="outline" size="sm" onClick={() => setUploadSheet(true)} data-testid="mobile-att-upload"><Upload size={16} />Carica</Button>
            </div>
            {atts.length ? (
              <div className="grid grid-cols-3 gap-2">
                {atts.map((a, idx) => (
                  <div key={a.id} className="group relative cursor-zoom-in overflow-hidden rounded-lg border border-border" data-testid={`mobile-att-${a.id}`} onClick={() => setLightbox(idx)}>
                    <img src={attUrl(a)} alt={a.filename} className="h-24 w-full object-cover" />
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/10"><ZoomIn size={16} className="text-white/90" /></div>
                    {a.source === "checklist" && <span className="absolute bottom-1 left-1 rounded bg-primary/90 px-1 py-0.5 text-[8px] font-bold text-primary-foreground">Check-list</span>}
                    <button onClick={(e) => { e.stopPropagation(); delAtt(a.id); }} className={cn("absolute right-1 top-1 rounded-md bg-white/90 p-1 text-destructive", locked && "hidden")}><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">Nessuna foto</p>}
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2"><MessageSquare size={16} className="text-primary" /><h3 className="text-sm font-bold text-navy">Note</h3></div>
            <div className="mb-3 flex gap-2">
              <Textarea rows={2} placeholder="Aggiungi una nota..." value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="mobile-note-input" />
              <Button onClick={addNote} data-testid="mobile-note-add" className="self-end"><Send size={16} /></Button>
            </div>
            <div className="space-y-2">
              {(t.notes || []).length ? t.notes.slice().reverse().map((n) => (
                <div key={n.id} className="rounded-lg border border-border bg-secondary/30 p-3" data-testid={`mobile-note-${n.id}`}>
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-sm font-bold text-navy">{n.author}</span>
                    <span className="text-xs text-muted-foreground">{fmtDateTime(n.at)}</span>
                    {n.edited_at && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">modificata{n.edited_by ? ` da ${n.edited_by}` : ""} · {fmtDateTime(n.edited_at)}</span>}
                    {canModifyNote(n) && editNoteId !== n.id && (
                      <span className="ml-auto flex items-center gap-2">
                        <button onClick={() => { setEditNoteId(n.id); setEditNoteText(n.text); }} className="text-muted-foreground active:text-primary" data-testid={`mobile-note-edit-${n.id}`}><Pencil size={15} /></button>
                        <button onClick={() => deleteNote(n.id)} className="text-muted-foreground active:text-destructive" data-testid={`mobile-note-del-${n.id}`}><Trash2 size={15} /></button>
                      </span>
                    )}
                  </div>
                  {editNoteId === n.id ? (
                    <div className="mt-1 space-y-2">
                      <Textarea rows={2} value={editNoteText} onChange={(e) => setEditNoteText(e.target.value)} data-testid={`mobile-note-edit-input-${n.id}`} />
                      <div className="flex gap-2">
                        <Button size="sm" className="flex-1" onClick={() => saveNoteEdit(n.id)} data-testid={`mobile-note-edit-save-${n.id}`}>Salva</Button>
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEditNoteId(null); setEditNoteText(""); }} data-testid={`mobile-note-edit-cancel-${n.id}`}>Annulla</Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-navy">{n.text}</p>
                  )}
                </div>
              )) : <p className="text-sm text-muted-foreground">Nessuna nota</p>}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>

    {/* Barra inferiore fissa (fuori dal contenitore animato per restare pinnata al viewport) */}
    <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-card p-3">
      {locked ? (
        <div className="flex items-center justify-center gap-2 rounded-xl bg-secondary/60 py-3 text-sm font-semibold text-muted-foreground" data-testid="mobile-status-locked">
          <Lock size={16} />Ticket completato — modifiche solo dal gestore
        </div>
      ) : (
        <Button className="h-12 w-full text-base" onClick={() => setSheetOpen(true)} data-testid="mobile-change-status-trigger">
          <ChevronsUpDown size={18} />Cambio Stato
        </Button>
      )}
    </div>

    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader><SheetTitle>Cambia stato · {t.code}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-2 pb-4">
          {statusOptions.filter((s) => !["sospeso_manutenzione", "da_sbloccare"].includes(s.v)).map((s) => (
            <button key={s.v} onClick={() => onSelectStatus(s.v)} data-testid={`mobile-status-${s.v}`}
              className={cn("flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-colors",
                t.status === s.v ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-navy")}>
              {s.l}{t.status === s.v && <span className="text-xs">attuale</span>}
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>

    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Completare il ticket {t.code}?</AlertDialogTitle>
          <AlertDialogDescription>
            I materiali installati verranno scaricati dal tuo magazzino mobile sulla commessa <strong>{t.id_olo ? `${t.code} - ${t.id_olo}` : t.code}</strong>. Dopo il completamento <strong>non potrai più modificare</strong> il consuntivo (potrai solo aggiungere foto e note).
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="mobile-complete-cancel">Annulla</AlertDialogCancel>
          <AlertDialogAction onClick={() => { setConfirmOpen(false); setChecklist({ caratterizzazione_done: null, caratterizzazione_reason: "", caratterizzazione_screenshot: "", srd_done: null, lavorazioni_eseguite: "" }); setChecklistOpen(true); }} data-testid="mobile-complete-confirm">Avanti · Check-list</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Firma di completamento */}
    <Dialog open={signOpen} onOpenChange={(o) => { if (!completing) setSignOpen(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Firma di completamento · {t.code}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-sm text-muted-foreground">Fai firmare il cliente qui sotto. La firma verrà applicata al documento del movimento di magazzino.</p>
          <SignaturePad onChange={setSig} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setSignOpen(false)} disabled={completing}>Annulla</Button>
          <Button onClick={submitCompletion} disabled={completing || !sig} data-testid="mobile-sign-confirm">
            {completing ? "Completamento…" : "Completa e scarica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Check-list di completamento */}
    <Dialog open={checklistOpen} onOpenChange={setChecklistOpen}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader><DialogTitle>Check-list di completamento · {t.code}</DialogTitle></DialogHeader>
        <div className="space-y-5 py-1">
          {/* Caratterizzazione */}
          <div>
            <p className="mb-2 text-sm font-semibold text-navy">È stata effettuata la caratterizzazione?</p>
            <div className="flex gap-2">
              <Button type="button" variant={checklist.caratterizzazione_done === true ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setChecklist((c) => ({ ...c, caratterizzazione_done: true, caratterizzazione_reason: "" }))} data-testid="cl-car-si">Sì</Button>
              <Button type="button" variant={checklist.caratterizzazione_done === false ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setChecklist((c) => ({ ...c, caratterizzazione_done: false }))} data-testid="cl-car-no">No</Button>
            </div>
            {checklist.caratterizzazione_done === false && (
              <Textarea className="mt-2" placeholder="Motivo per cui non è possibile effettuare la caratterizzazione…" value={checklist.caratterizzazione_reason} onChange={(e) => setChecklist((c) => ({ ...c, caratterizzazione_reason: e.target.value }))} data-testid="cl-car-reason" />
            )}
            {checklist.caratterizzazione_done !== null && (
              <div className="mt-2">
                <input ref={clFileRef} type="file" accept="image/*" className="hidden" onChange={onChecklistFile} data-testid="cl-car-file" />
                <Button type="button" variant="outline" size="sm" onClick={() => clFileRef.current?.click()} data-testid="cl-car-upload">
                  <ImageIcon size={14} />{checklist.caratterizzazione_screenshot ? "Screenshot caricato ✓" : "Carica screenshot"}
                </Button>
                {checklist.caratterizzazione_screenshot && <img src={checklist.caratterizzazione_screenshot} alt="screen" className="mt-2 h-24 rounded-lg border border-border object-cover" />}
              </div>
            )}
          </div>
          {/* Scheda SRD */}
          <div>
            <p className="mb-2 text-sm font-semibold text-navy">È stata effettuata la scheda SRD?</p>
            <div className="flex gap-2">
              <Button type="button" variant={checklist.srd_done === true ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setChecklist((c) => ({ ...c, srd_done: true }))} data-testid="cl-srd-si">Sì</Button>
              <Button type="button" variant={checklist.srd_done === false ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setChecklist((c) => ({ ...c, srd_done: false }))} data-testid="cl-srd-no">No</Button>
            </div>
          </div>
          {/* Lavorazioni eseguite */}
          <div>
            <p className="mb-2 text-sm font-semibold text-navy">Lavorazioni eseguite *</p>
            <Textarea placeholder="Descrivi le lavorazioni eseguite…" value={checklist.lavorazioni_eseguite} onChange={(e) => setChecklist((c) => ({ ...c, lavorazioni_eseguite: e.target.value }))} data-testid="cl-lavorazioni" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setChecklistOpen(false)}>Annulla</Button>
          <Button onClick={proceedFromChecklist} data-testid="cl-proceed">Avanti · Firma</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Motivo sospensione */}
    <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Sospendi ticket · {t.code}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label>Motivo sospensione *</Label>
            <Select value={suspendMotivo} onValueChange={setSuspendMotivo}>
              <SelectTrigger data-testid="mobile-suspend-motivo"><SelectValue placeholder="Seleziona un motivo…" /></SelectTrigger>
              <SelectContent>{suspensionReasons.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Nota *</Label>
            <Textarea placeholder="Descrivi la sospensione (verrà salvata nello storico)…" value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} data-testid="mobile-suspend-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setSuspendOpen(false)}>Annulla</Button>
          <Button onClick={submitSuspend} data-testid="mobile-suspend-confirm">Sospendi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Dialog KO */}
    <Dialog open={koOpen} onOpenChange={setKoOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Imposta KO · {t.code}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label>Motivo del KO *</Label>
            <Select value={koMotivo} onValueChange={setKoMotivo}>
              <SelectTrigger data-testid="mobile-ko-motivo"><SelectValue placeholder="Seleziona un motivo…" /></SelectTrigger>
              <SelectContent>{koReasons.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Nota *</Label>
            <Textarea placeholder="Descrivi il motivo del KO (verrà salvato nello storico)…" value={koNote} onChange={(e) => setKoNote(e.target.value)} data-testid="mobile-ko-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setKoOpen(false)}>Annulla</Button>
          <Button onClick={submitKo} data-testid="mobile-ko-confirm">Conferma KO</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Scelta sorgente foto */}
    <Sheet open={uploadSheet} onOpenChange={setUploadSheet}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader><SheetTitle>Carica foto</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-2 pb-4">
          <button onClick={() => { setUploadSheet(false); fileRef.current?.click(); }} data-testid="mobile-upload-camera"
            className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left text-sm font-semibold text-navy">
            <Camera size={20} className="text-primary" />Scatta con la fotocamera
          </button>
          <button onClick={() => { setUploadSheet(false); galleryRef.current?.click(); }} data-testid="mobile-upload-gallery"
            className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left text-sm font-semibold text-navy">
            <Images size={20} className="text-primary" />Scegli dalla galleria
          </button>
        </div>
      </SheetContent>
    </Sheet>

    <AttachmentLightbox attachments={atts} index={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}
